'use client'
import * as React from 'react'
import { uniqBy } from 'utils/array'
import { m, LazyMotion, domAnimation, useAnimationControls } from 'framer-motion'
import type { MenuItem } from 'instantsearch.js/es/connectors/menu/connectMenu'
import { tw } from 'utils/classnames'
import {
  SearchIcon,
  LayoutAlt04Icon,
  ChevronDownIcon,
  SearchMdIcon,
  CheckIcon,
} from 'primitives/icons'
import { useOnClickOutside } from 'hooks/use-on-click-outside'
import type { SearchInputProps } from '../search-input'
import { searchForFacets } from '../algolia-utils/search-for-facets'

function capitalize(str: string) {
  if (!str) {
    return ''
  }
  return str[0].toUpperCase() + str.slice(1).toLowerCase()
}

export function StudioTemplatesSearchInputIcon({
  className = '',
  ...rest
}: React.HTMLAttributes<SVGElement>) {
  return <SearchIcon className={tw(`h-6 w-6 text-gray-400 ${className}`)} {...rest} />
}

export function StudioTemplatesSearchInputButton({
  className = '',
  ...rest
}: React.ComponentProps<'button'>) {
  return (
    <button type="button" className="text-16 sm:text-14 flex gap-2 pr-1 text-gray-400" {...rest}>
      Search
    </button>
  )
}

const variants = {
  expanded: { opacity: 1, height: 'auto', transition: { duration: 0.1 } },
  collapsed: { opacity: 0, height: 0, transition: { duration: 0.1 } },
}
export function StudioTemplatesSearchInput({
  children,
  showResults = false,
  setShowResults,
  containerClassName,
  className,
  resultsClassName,
  selectedCategory,
  setSelectedCategory,
  InputIcon = <StudioTemplatesSearchInputIcon />,
  InputButton = <StudioTemplatesSearchInputButton />,
  withoutResults = false,
  ...inputProps
}: SearchInputProps) {
  const controls = useAnimationControls()
  const categoryControls = useAnimationControls()
  const [categories, setCategories] = React.useState<MenuItem[]>([])
  const [categoryTerm, setCategoryTerm] = React.useState('')
  const filteredCategories = React.useMemo(
    () =>
      categoryTerm
        ? categories?.filter(category =>
            category.value.toLowerCase().includes(categoryTerm.toLowerCase()),
          )
        : categories,
    [categories, categoryTerm],
  )
  const [shouldOpenCategorySearch, setShouldOpenCategorySearch] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)
  const categoryRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const getCategories = async () => {
      let allCategories: MenuItem[] = []
      let hasError = false

      try {
        allCategories = await searchForFacets()
      } catch (error) {
        hasError = true
      } finally {
        if (!hasError && allCategories?.length > 0) {
          const uniqueCategories = uniqBy(allCategories, 'label')
          // oxlint-disable-next-line unicorn/no-array-sort
          setCategories(uniqueCategories.sort((a, b) => a.value.localeCompare(b.value)))
        }
      }
    }

    getCategories()
  }, [])

  React.useEffect(() => {
    controls.start(!shouldOpenCategorySearch && showResults ? 'expanded' : 'collapsed')
  }, [showResults, shouldOpenCategorySearch, controls])

  React.useEffect(() => {
    if (shouldOpenCategorySearch) {
      setShowResults?.(false)
    }

    categoryControls.start(shouldOpenCategorySearch ? 'expanded' : 'collapsed')
  }, [shouldOpenCategorySearch, categoryControls, setShowResults])

  useOnClickOutside(ref, () => {
    setShouldOpenCategorySearch(false)
    setShowResults?.(false)
  })

  useOnClickOutside(categoryRef, () => {
    setShouldOpenCategorySearch(false)
    setCategoryTerm('')
  })

  return (
    <div ref={ref} className="relative w-full">
      <div className="hidden sm:contents">
        {shouldOpenCategorySearch ? (
          <div ref={categoryRef} className="contents">
            <label
              className={tw(
                'border-gray-600-300 border-r-gray-700-opacity absolute flex h-14 w-[200px] gap-2 border p-2',
                'bg-service-white rounded-tl-2 items-center justify-start text-gray-600',
              )}
            >
              <div className="rounded-2 relative flex h-10 min-w-[184px] bg-[#eeeff2]">
                <SearchMdIcon className="ml-3 mr-2 mt-[12px] size-4 scale-[1.125] text-gray-500" />
                <input
                  className="rounded-2 text-14 font-inter w-full bg-[#eeeff2] py-[10px] pr-3 text-gray-600 placeholder-[#4b5563] focus:outline-none"
                  placeholder="Search for templates"
                  onChange={e => setCategoryTerm(e.target.value)}
                />
              </div>
            </label>
            <LazyMotion features={domAnimation}>
              <m.ul
                initial={showResults ? 'expanded' : 'collapsed'}
                animate={categoryControls}
                variants={variants}
                style={{
                  '--scroller-size': '4px',
                  '--scroller-thumb-color': '#d1d5db',
                }}
                className={tw(
                  // oxlint-disable-next-line no-negated-condition
                  !showResults ? 'z-0 hidden' : 'z-10',
                  'rounded-b-2 bg-service-white absolute mt-14 flex w-[200px] flex-col gap-1 border border-t-0 border-gray-700 p-3',
                  'border-gray-600-300 bg-cf-white font-inter scroller-xs max-h-[416px]',
                )}
              >
                <SearchInputMenuRow
                  isSelected={!selectedCategory}
                  onClick={() => setSelectedCategory?.('')}
                >
                  All Templates
                </SearchInputMenuRow>
                {setSelectedCategory && filteredCategories?.length > 0
                  ? filteredCategories.map(({ value, label }) => (
                      <SearchInputMenuRow
                        key={label}
                        isSelected={selectedCategory?.toLowerCase() === value.toLowerCase()}
                        onClick={() => setSelectedCategory(value)}
                      >
                        {capitalize(value)}
                      </SearchInputMenuRow>
                    ))
                  : null}
              </m.ul>
            </LazyMotion>
          </div>
        ) : (
          <div
            className={tw(
              'border-gray-600-300 border-r-gray-700-opacity absolute flex h-14 w-[200px] gap-2 border p-4',
              'bg-service-white items-center text-gray-600',
              showResults && children ? 'rounded-tl-2 rounded-b-none' : 'rounded-l-2',
            )}
          >
            <div className="flex w-full justify-between">
              <div className="flex gap-2">
                <LayoutAlt04Icon className="size-6 min-w-6 justify-start" />
                <span className="font-poppins my-auto max-w-[104px] justify-start truncate font-[500] text-gray-600">
                  {selectedCategory || 'All Templates'}
                </span>
              </div>
              <button type="button" className="flex size-6 w-fit items-center justify-end self-end">
                <ChevronDownIcon
                  className="flex size-4 w-fit items-center justify-end text-right text-gray-300"
                  onClick={() => setShouldOpenCategorySearch(true)}
                />
              </button>
            </div>
          </div>
        )}
      </div>
      <label
        className={tw(
          showResults && children
            ? 'rounded-t-2 sm:rounded-r-2 sm:rounded-b-[unset] sm:rounded-l-[unset]'
            : 'rounded-2 sm:rounded-r-2 sm:rounded-[unset]',
          'bg-service-white border-gray-600-300 flex items-center justify-between gap-2 self-stretch border border-l-0 p-4 sm:ml-[200px]',
          containerClassName,
        )}
      >
        <input
          className={tw(`w-full text-gray-600 placeholder-gray-400 focus:outline-none`, className)}
          {...inputProps}
        />
        {InputButton}
      </label>
      <LazyMotion features={domAnimation}>
        <m.div
          initial={showResults ? 'expanded' : 'collapsed'}
          animate={controls}
          variants={variants}
          className={
            shouldOpenCategorySearch
              ? 'pointer-events-none z-0 hidden'
              : tw(
                  showResults ? 'z-10' : 'pointer-events-none z-0 hidden',
                  !withoutResults &&
                    'rounded-b-2 bg-service-white absolute flex w-full flex-col self-stretch border border-t-0 border-gray-700 p-2',
                  resultsClassName,
                )
          }
        >
          {children}
        </m.div>
      </LazyMotion>
    </div>
  )
}

interface SearchInputMenuRowProps extends React.ComponentProps<'button'> {
  isSelected?: boolean
}
function SearchInputMenuRow({ isSelected, children, ...props }: SearchInputMenuRowProps) {
  return (
    <button
      type="button"
      className={tw(
        'rounded-2 text-14 flex min-h-[40px] w-full cursor-pointer items-center justify-start text-nowrap text-left font-[500] text-[#1A1C1F] outline-none',
        'font-inter hover:text-cf-white justify-between p-2 pr-4 hover:bg-violet-700 focus-visible:ring-2 focus-visible:ring-violet-500',
        isSelected && 'text-cf-white gap-2 bg-violet-700',
      )}
      {...props}
    >
      {isSelected ? <LayoutAlt04Icon className="size-6 shrink-0" /> : null}
      <span
        className={tw(
          'flex w-full max-w-[174px] justify-start truncate text-left',
          isSelected && 'block max-w-[88px]',
        )}
      >
        {children}
      </span>
      {isSelected ? <CheckIcon className="size-6 shrink-0" /> : null}
    </button>
  )
}

export function StudioTemplatesSearchInputFallback({
  containerClassName,
  className,
  InputIcon = <StudioTemplatesSearchInputIcon />,
  InputButton = <StudioTemplatesSearchInputButton />,
  ...inputProps
}: SearchInputProps) {
  return (
    <div
      className={tw(
        'rounded-2 bg-service-white flex w-full items-center justify-between gap-2 self-stretch border border-gray-300 p-4',
        containerClassName,
      )}
    >
      <input
        className={tw(`w-full text-gray-600 placeholder-gray-400 focus:outline-none`, className)}
        {...inputProps}
      />
      {InputButton}
    </div>
  )
}
